import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Sun, Mail, Lock, User, Building2, Phone, FileText, Eye, EyeOff,
    MapPin, CreditCard, Upload, CheckCircle, ChevronRight, ChevronLeft,
    Shield, Award, FileCheck, AlertCircle, Loader2, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const INDIAN_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
];

const BUSINESS_TYPES = [
    { value: 'manufacturer', label: 'Manufacturer' },
    { value: 'distributor', label: 'Distributor' },
    { value: 'retailer', label: 'Retailer' },
    { value: 'installer', label: 'Installer/Service Provider' },
    { value: 'reseller', label: 'Authorized Reseller' },
    { value: 'epc_developer', label: 'EPC Developer' }
];

const FOCUS_CUSTOMERS = [
    { value: 'pm_surya_ghar', label: 'PM Surya Ghar Yojana' },
    { value: 'solar_epc_projects', label: 'Solar EPC Projects' },
    { value: 'without_subsidy', label: 'Without Subsidy Solar Services' }
];

const FOCUS_GEOGRAPHY = [
    { value: 'grid_connected_inverters', label: 'Grid-Connected Inverters' },
    { value: 'grid_solar_rooftop', label: 'Grid Solar Rooftop System' },
    { value: 'hybrid_solar_rooftop', label: 'Hybrid Solar Rooftop System' }
];

const ACCOUNT_TYPES = [
    { value: 'savings', label: 'Savings Account' },
    { value: 'current', label: 'Current Account' },
    { value: 'overdraft', label: 'Overdraft Account' }
];

const VendorRegisterPage = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [showPassword, setShowPassword] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(null);

    // Form state
    const [form, setForm] = useState({
        // Step 1: Basic Information
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',

        // Step 2: Business Details
        business_name: '',
        business_type: '',
        focus_customer: '',
        focus_geography: '',
        tech_capabilities: '',
        gst_number: '',
        pan_number: '',
        business_address: '',
        city: '',
        state: '',
        pincode: '',
        description: '',

        // Step 3: Bank Details
        bank_details: {
            bank_name: '',
            account_holder_name: '',
            account_number: '',
            confirm_account_number: '',
            ifsc_code: '',
            branch_name: '',
            account_type: ''
        },

        // Step 4 & 5: Documents
        documents: {
            pan_card_url: null,
            gst_certificate_url: null,
            bis_certification_url: null,
            mnre_registration_url: null,
            discom_registration_url: null,
            nabl_test_reports_url: null,
            manufacturer_authorization_url: null,
            bill_of_entry_url: null,
            iec_code_url: null,
            company_registration_url: null,
            vendor_agreement_url: null
        }
    });

    const steps = [
        { id: 1, title: 'Basic Info', icon: User },
        { id: 2, title: 'Business Details', icon: Building2 },
        { id: 3, title: 'Bank Details', icon: CreditCard },
        { id: 4, title: 'Certifications', icon: Shield },
        { id: 5, title: 'Documents', icon: FileCheck }
    ];

    const uploadFile = async (file, fieldName) => {
        setUploadingFile(fieldName);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axios.post(`${API}/api/upload/public`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setForm(prev => ({
                ...prev,
                documents: {
                    ...prev.documents,
                    [fieldName]: response.data.url
                }
            }));
            toast.success(`${file.name} uploaded successfully`);
        } catch (error) {
            toast.error('File upload failed');
        } finally {
            setUploadingFile(null);
        }
    };

    const removeFile = (fieldName) => {
        setForm(prev => ({
            ...prev,
            documents: {
                ...prev.documents,
                [fieldName]: null
            }
        }));
    };

    const validateStep = (step) => {
        switch (step) {
            case 1:
                if (!form.name || !form.email || !form.password || !form.phone) {
                    toast.error('Please fill all required fields');
                    return false;
                }
                if (form.password !== form.confirmPassword) {
                    toast.error('Passwords do not match');
                    return false;
                }
                if (form.password.length < 6) {
                    toast.error('Password must be at least 6 characters');
                    return false;
                }
                return true;
            case 2:
                if (!form.business_name || !form.business_type || !form.gst_number ||
                    !form.pan_number || !form.business_address || !form.city ||
                    !form.state || !form.pincode) {
                    toast.error('Please fill all required fields');
                    return false;
                }
                if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gst_number)) {
                    toast.error('Please enter a valid GST number');
                    return false;
                }
                if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan_number)) {
                    toast.error('Please enter a valid PAN number');
                    return false;
                }
                return true;
            case 3:
                const bank = form.bank_details;
                if (!bank.bank_name || !bank.account_holder_name || !bank.account_number || !bank.ifsc_code) {
                    toast.error('Please fill all required bank details');
                    return false;
                }
                if (bank.account_number !== bank.confirm_account_number) {
                    toast.error('Account numbers do not match');
                    return false;
                }
                if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bank.ifsc_code)) {
                    toast.error('Please enter a valid IFSC code');
                    return false;
                }
                return true;
            case 4:
                if (!form.documents.pan_card_url || !form.documents.gst_certificate_url ||
                    !form.documents.bis_certification_url || !form.documents.mnre_registration_url ||
                    !form.documents.discom_registration_url) {
                    toast.error('Please upload all mandatory certifications');
                    return false;
                }
                return true;
            case 5:
                return true;
            default:
                return true;
        }
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => Math.min(prev + 1, 5));
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    const handleSubmit = async () => {
        if (!acceptTerms) {
            toast.error('Please accept the terms and conditions');
            return;
        }

        setLoading(true);
        try {
            const submitData = {
                ...form,
                bank_details: {
                    bank_name: form.bank_details.bank_name,
                    account_holder_name: form.bank_details.account_holder_name,
                    account_number: form.bank_details.account_number,
                    ifsc_code: form.bank_details.ifsc_code,
                    branch_name: form.bank_details.branch_name || null,
                    account_type: form.bank_details.account_type || null
                }
            };
            delete submitData.confirmPassword;
            delete submitData.bank_details.confirm_account_number;

            await axios.post(`${API}/api/vendors/register`, submitData);
            navigate('/vendor-registration-success');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const FileUploadField = ({ label, fieldName, required = false, hint }) => (
        <div className="space-y-2">
            <Label className="flex items-center gap-2">
                {label} {required && <span className="text-red-500">*</span>}
            </Label>
            {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
            {form.documents[fieldName] ? (
                <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">Uploaded</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeFile(fieldName)}>
                        <X className="w-4 h-4" />
                    </Button>
                </div>
            ) : (
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 transition-colors bg-muted/30">
                    <div className="flex items-center gap-2 text-muted-foreground">
                        {uploadingFile === fieldName ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <Upload className="w-5 h-5" />
                        )}
                        <span className="text-sm">{uploadingFile === fieldName ? 'Uploading...' : 'Click to upload'}</span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG (Max 5MB)</span>
                    <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], fieldName)}
                        disabled={uploadingFile === fieldName}
                    />
                </label>
            )}
        </div>
    );

    return (
        <div className="min-h-screen md:pt-20 py-12 px-4 bg-gradient-to-b from-background to-muted/20 -mt-14 pt-14 md:mt-0">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-3xl mx-auto"
            >
                {/* Header */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2 mb-6">
                        <Sun className="w-10 h-10 text-primary" />
                        <span className="text-2xl font-bold"><span className="solar-text-gradient">Solar</span>Savers</span>
                    </Link>
                    <h1 className="text-2xl md:text-3xl font-bold">Become a Verified Vendor</h1>
                    <p className="text-muted-foreground mt-2">Join India's leading solar marketplace</p>
                </div>

                {/* Progress Steps */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        {steps.map((step, index) => (
                            <div key={step.id} className="flex-1 flex items-center">
                                <div className="flex flex-col items-center">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${currentStep >= step.id
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted text-muted-foreground'
                                        }`}>
                                        {currentStep > step.id ? (
                                            <CheckCircle className="w-5 h-5" />
                                        ) : (
                                            <step.icon className="w-5 h-5" />
                                        )}
                                    </div>
                                    <span className={`text-xs mt-2 hidden md:block ${currentStep >= step.id ? 'text-primary font-medium' : 'text-muted-foreground'
                                        }`}>
                                        {step.title}
                                    </span>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`flex-1 h-1 mx-2 rounded ${currentStep > step.id ? 'bg-primary' : 'bg-muted'
                                        }`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Form Card */}
                <div className="bg-card rounded-2xl border shadow-lg p-6 md:p-8">
                    <AnimatePresence mode="wait">
                        {/* Step 1: Basic Information */}
                        {currentStep === 1 && (
                            <motion.div
                                key="step1"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-6"
                            >
                                <div>
                                    <h2 className="text-xl font-semibold flex items-center gap-2">
                                        <User className="w-5 h-5 text-primary" />
                                        Basic Information
                                    </h2>
                                    <p className="text-sm text-muted-foreground mt-1">Your contact details</p>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="name">Contact Person Name *</Label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                            <Input
                                                id="name"
                                                placeholder="Your full name"
                                                value={form.name}
                                                onChange={e => setForm({ ...form, name: e.target.value })}
                                                className="pl-10"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email Address *</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder="you@company.com"
                                                value={form.email}
                                                onChange={e => setForm({ ...form, email: e.target.value })}
                                                className="pl-10"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone Number *</Label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="phone"
                                            placeholder="+91 9876543210"
                                            value={form.phone}
                                            onChange={e => setForm({ ...form, phone: e.target.value })}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="password">Password *</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                            <Input
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="••••••••"
                                                value={form.password}
                                                onChange={e => setForm({ ...form, password: e.target.value })}
                                                className="pl-10 pr-10"
                                            />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword">Confirm Password *</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                            <Input
                                                id="confirmPassword"
                                                type="password"
                                                placeholder="••••••••"
                                                value={form.confirmPassword}
                                                onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                                                className="pl-10"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Step 2: Business Details */}
                        {currentStep === 2 && (
                            <motion.div
                                key="step2"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-6"
                            >
                                <div>
                                    <h2 className="text-xl font-semibold flex items-center gap-2">
                                        <Building2 className="w-5 h-5 text-primary" />
                                        Business Details
                                    </h2>
                                    <p className="text-sm text-muted-foreground mt-1">Your company information</p>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="business_name">Business/Company Name *</Label>
                                        <Input
                                            id="business_name"
                                            placeholder="ABC Solar Solutions Pvt Ltd"
                                            value={form.business_name}
                                            onChange={e => setForm({ ...form, business_name: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Business Type *</Label>
                                        <Select value={form.business_type} onValueChange={v => setForm({ ...form, business_type: v })}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select business type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {BUSINESS_TYPES.map(type => (
                                                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Focus Customer *</Label>
                                        <Select value={form.focus_customer} onValueChange={v => setForm({ ...form, focus_customer: v })}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select focus customer" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {FOCUS_CUSTOMERS.map(item => (
                                                    <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">Primary customer segment you serve</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Focus Geography / Expertise *</Label>
                                        <Select value={form.focus_geography} onValueChange={v => setForm({ ...form, focus_geography: v })}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select expertise area" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {FOCUS_GEOGRAPHY.map(item => (
                                                    <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">Your primary technology expertise</p>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="gst_number">GST Number *</Label>
                                        <Input
                                            id="gst_number"
                                            placeholder="22AAAAA0000A1Z5"
                                            value={form.gst_number}
                                            onChange={e => setForm({ ...form, gst_number: e.target.value.toUpperCase() })}
                                            maxLength={15}
                                        />
                                        <p className="text-xs text-muted-foreground">15-character GST Identification Number</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="pan_number">PAN Number *</Label>
                                        <Input
                                            id="pan_number"
                                            placeholder="AAAAA0000A"
                                            value={form.pan_number}
                                            onChange={e => setForm({ ...form, pan_number: e.target.value.toUpperCase() })}
                                            maxLength={10}
                                        />
                                        <p className="text-xs text-muted-foreground">10-character Permanent Account Number</p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="business_address">Business Address *</Label>
                                    <Textarea
                                        id="business_address"
                                        rows={2}
                                        placeholder="Street address, building name, etc."
                                        value={form.business_address}
                                        onChange={e => setForm({ ...form, business_address: e.target.value })}
                                    />
                                </div>

                                <div className="grid md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="city">City *</Label>
                                        <Input
                                            id="city"
                                            placeholder="Mumbai"
                                            value={form.city}
                                            onChange={e => setForm({ ...form, city: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>State *</Label>
                                        <Select value={form.state} onValueChange={v => setForm({ ...form, state: v })}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select state" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {INDIAN_STATES.map(state => (
                                                    <SelectItem key={state} value={state}>{state}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="pincode">Pincode *</Label>
                                        <Input
                                            id="pincode"
                                            placeholder="400001"
                                            value={form.pincode}
                                            onChange={e => setForm({ ...form, pincode: e.target.value })}
                                            maxLength={6}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="description">Business Description (Optional)</Label>
                                    <Textarea
                                        id="description"
                                        rows={3}
                                        placeholder="Brief description of your solar business, products, and services..."
                                        value={form.description}
                                        onChange={e => setForm({ ...form, description: e.target.value })}
                                    />
                                </div>
                            </motion.div>
                        )}

                        {/* Step 3: Bank Details */}
                        {currentStep === 3 && (
                            <motion.div
                                key="step3"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-6"
                            >
                                <div>
                                    <h2 className="text-xl font-semibold flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-primary" />
                                        Bank Account Details
                                    </h2>
                                    <p className="text-sm text-muted-foreground mt-1">For payment settlements</p>
                                </div>

                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                    <div className="flex gap-3">
                                        <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Secure Banking Information</p>
                                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                Your bank details are encrypted and secure. This information is used only for payment settlements.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="bank_name">Bank Name *</Label>
                                        <Input
                                            id="bank_name"
                                            placeholder="State Bank of India"
                                            value={form.bank_details.bank_name}
                                            onChange={e => setForm({
                                                ...form,
                                                bank_details: { ...form.bank_details, bank_name: e.target.value }
                                            })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="account_holder_name">Account Holder Name *</Label>
                                        <Input
                                            id="account_holder_name"
                                            placeholder="As per bank records"
                                            value={form.bank_details.account_holder_name}
                                            onChange={e => setForm({
                                                ...form,
                                                bank_details: { ...form.bank_details, account_holder_name: e.target.value }
                                            })}
                                        />
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Account Type *</Label>
                                        <Select value={form.bank_details.account_type} onValueChange={v => setForm({
                                            ...form,
                                            bank_details: { ...form.bank_details, account_type: v }
                                        })}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select account type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ACCOUNT_TYPES.map(type => (
                                                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="account_number">Account Number *</Label>
                                        <Input
                                            id="account_number"
                                            type="password"
                                            placeholder="Enter account number"
                                            value={form.bank_details.account_number}
                                            onChange={e => setForm({
                                                ...form,
                                                bank_details: { ...form.bank_details, account_number: e.target.value }
                                            })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="confirm_account_number">Confirm Account Number *</Label>
                                        <Input
                                            id="confirm_account_number"
                                            placeholder="Re-enter account number"
                                            value={form.bank_details.confirm_account_number}
                                            onChange={e => setForm({
                                                ...form,
                                                bank_details: { ...form.bank_details, confirm_account_number: e.target.value }
                                            })}
                                        />
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="ifsc_code">IFSC Code *</Label>
                                        <Input
                                            id="ifsc_code"
                                            placeholder="SBIN0001234"
                                            value={form.bank_details.ifsc_code}
                                            onChange={e => setForm({
                                                ...form,
                                                bank_details: { ...form.bank_details, ifsc_code: e.target.value.toUpperCase() }
                                            })}
                                            maxLength={11}
                                        />
                                        <p className="text-xs text-muted-foreground">11-character IFSC code</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="branch_name">Branch Name (Optional)</Label>
                                        <Input
                                            id="branch_name"
                                            placeholder="Main Branch, Mumbai"
                                            value={form.bank_details.branch_name}
                                            onChange={e => setForm({
                                                ...form,
                                                bank_details: { ...form.bank_details, branch_name: e.target.value }
                                            })}
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Step 4: Certifications */}
                        {currentStep === 4 && (
                            <motion.div
                                key="step4"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-6"
                            >
                                <div>
                                    <h2 className="text-xl font-semibold flex items-center gap-2">
                                        <Shield className="w-5 h-5 text-primary" />
                                        Mandatory Certifications
                                    </h2>
                                    <p className="text-sm text-muted-foreground mt-1">Required documents for verification</p>
                                </div>

                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                                    <div className="flex gap-3">
                                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">All documents are mandatory</p>
                                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                                Upload clear, legible copies. Accepted formats: PDF, JPG, PNG (Max 5MB each)
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <FileUploadField
                                        label="PAN Card"
                                        fieldName="pan_card_url"
                                        required
                                        hint="Company/Individual PAN card"
                                    />
                                    <FileUploadField
                                        label="GST Certificate"
                                        fieldName="gst_certificate_url"
                                        required
                                        hint="GST registration certificate"
                                    />
                                    <FileUploadField
                                        label="BIS Certification"
                                        fieldName="bis_certification_url"
                                        required
                                        hint="Bureau of Indian Standards certification for solar panels/inverters/batteries"
                                    />
                                    <FileUploadField
                                        label="MNRE Registration"
                                        fieldName="mnre_registration_url"
                                        required
                                        hint="Ministry of New and Renewable Energy registration"
                                    />
                                    <FileUploadField
                                        label="DISCOM Registration"
                                        fieldName="discom_registration_url"
                                        required
                                        hint="Registration with local Distribution Company (DISCOM)"
                                    />
                                </div>

                                <div className="pt-4 border-t">
                                    <h3 className="font-medium mb-4 flex items-center gap-2">
                                        <Award className="w-4 h-4 text-primary" />
                                        Optional but Recommended
                                    </h3>
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <FileUploadField
                                            label="NABL Test Reports"
                                            fieldName="nabl_test_reports_url"
                                            hint="Product test reports from NABL accredited labs"
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Step 5: Additional Documents */}
                        {currentStep === 5 && (
                            <motion.div
                                key="step5"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-6"
                            >
                                <div>
                                    <h2 className="text-xl font-semibold flex items-center gap-2">
                                        <FileCheck className="w-5 h-5 text-primary" />
                                        Additional Documents
                                    </h2>
                                    <p className="text-sm text-muted-foreground mt-1">Optional documents (if applicable)</p>
                                </div>

                                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                    <div className="flex gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-green-800 dark:text-green-300">Almost done!</p>
                                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                                Upload any additional documents that apply to your business
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Vendor Agreement - MANDATORY - shown FIRST */}
                                <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-400 dark:border-amber-600 rounded-xl p-5">
                                    <h3 className="font-bold text-base flex items-center gap-2 text-amber-800 dark:text-amber-300 mb-1">
                                        <AlertCircle className="w-5 h-5" />
                                        Vendor Agreement Upload <span className="text-red-500">*</span>
                                    </h3>
                                    <p className="text-xs text-amber-700 dark:text-amber-400 mb-4">
                                        Download, sign, and upload the SolarSavers Vendor Agreement. This is <strong>mandatory</strong> to complete registration.
                                    </p>
                                    <FileUploadField
                                        label="Signed Vendor Agreement"
                                        fieldName="vendor_agreement_url"
                                        required
                                        hint="Upload your signed copy of the Vendor Agreement (PDF, JPG, PNG – Max 5MB)"
                                    />
                                </div>

                                {/* Optional Docs */}
                                <div className="grid md:grid-cols-2 gap-6">
                                    <FileUploadField
                                        label="Manufacturer Authorization Letter"
                                        fieldName="manufacturer_authorization_url"
                                        hint="Required if you are a reseller/distributor"
                                    />
                                    <FileUploadField
                                        label="Company Registration"
                                        fieldName="company_registration_url"
                                        hint="Certificate of Incorporation or Partnership Deed"
                                    />
                                </div>

                                <div className="pt-4 border-t">
                                    <h3 className="font-medium mb-4">For Imported Products</h3>
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <FileUploadField
                                            label="Bill of Entry"
                                            fieldName="bill_of_entry_url"
                                            hint="Customs import document"
                                        />
                                        <FileUploadField
                                            label="IEC Code Certificate"
                                            fieldName="iec_code_url"
                                            hint="Import Export Code from DGFT"
                                        />
                                    </div>
                                </div>

                                <div className="pt-6 border-t">
                                    <div className="flex items-start gap-3">
                                        <Checkbox id="terms" checked={acceptTerms} onCheckedChange={setAcceptTerms} />
                                        <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer">
                                            I agree to the <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>,
                                            <Link to="/privacy" className="text-primary hover:underline"> Privacy Policy</Link>, and
                                            <Link to="/vendor-agreement" className="text-primary hover:underline"> Vendor Agreement</Link>.
                                            I confirm that all information provided is accurate and all documents are genuine.
                                        </label>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Navigation Buttons */}
                    <div className="flex justify-between mt-8 pt-6 border-t">
                        <Button
                            variant="outline"
                            onClick={prevStep}
                            disabled={currentStep === 1}
                            className="gap-2"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Previous
                        </Button>

                        {currentStep < 5 ? (
                            <Button onClick={nextStep} className="btn-primary gap-2">
                                Next Step
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        ) : (
                            <Button
                                onClick={handleSubmit}
                                disabled={loading || !acceptTerms}
                                className="btn-primary gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Submit Application
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Already registered */}
                <div className="mt-6 text-center text-sm">
                    <span className="text-muted-foreground">Already registered? </span>
                    <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
                </div>
            </motion.div>
        </div>
    );
};

export default VendorRegisterPage;
